/**
 * POST /allocation-questionnaire/strategy/submit — save strategy answers.
 *
 * Belongs here: path, axios, payload builder, mutation, toast + status refetch on save.
 * Does not belong here: navigation or session commit (callers own those).
 */
import { useCallback, useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '../../../../utils/axiosConfig'
import { mobileBackendUrl } from '../../../../env'
import { useToast } from '../../../../state-management/contexts/ToastContext'
import type {
  StrategyQuestionnaireAnswersMap,
  SubmitStrategyQuestionnaireApiRequest,
  SubmitQuestionInput
} from '../shared/types/questionnaire.types'
import { runExpertReviewSubmit, EXPERT_REVIEW_SAVE_ERROR_TOAST } from './runExpertReviewSubmit'
import {
  createAnswersSubmitQueue,
  enqueueAnswersSubmit,
  type AnswersSubmitQueue
} from './enqueueAnswersSubmit'

export const STRATEGY_ANSWERS_SUBMIT_PATH = `${mobileBackendUrl}/v2/portfolio/allocation-questionnaire/strategy/submit`

/** Builds submit payload from the in-memory answers map. */
export function buildStrategyQuestionInputs(
  answers: StrategyQuestionnaireAnswersMap
): SubmitQuestionInput[] {
  return Object.entries(answers).map(([questionId, value]) => {
    const input: SubmitQuestionInput = { questionId }
    // API shape differs by input type — number vs single/multi option ids
    if (typeof value === 'number') {
      input.inputValueDouble = value
    } else if (Array.isArray(value)) {
      input.optionId = value.length > 0 ? value : undefined
    } else {
      input.optionId = (value ?? '') !== '' ? [String(value)] : undefined
    }
    return input
  })
}

export type SubmitStrategyAnswersVars = {
  questionnaireId: string
  answers: StrategyQuestionnaireAnswersMap
  /** Snapshot of committedAnswersVersion at POST time — MarkSubmitted uses this, not live state. */
  answersVersion: number
}

/** POST strategy questionnaire answers. */
export async function submitStrategyQuestionnaireAnswers({
  questionnaireId,
  answers
}: SubmitStrategyAnswersVars): Promise<void> {
  const body: SubmitStrategyQuestionnaireApiRequest = {
    questionnaireId,
    questionInputs: buildStrategyQuestionInputs(answers)
  }
  await axiosInstance.post(STRATEGY_ANSWERS_SUBMIT_PATH, body)
}

type UseSubmitStrategyAnswersMutationOptions = {
  /**
   * Fired with vars.answersVersion only after the wrapper fully succeeds
   * (POST for submitAnswers; POST + status for submitAnswersAndStatusRefresh).
   */
  onSubmittedAnswersVersion?: (answersVersion: number) => void
}

/**
 * Strategy answers mutation.
 * `submitAnswers` = POST only (resolve needs status after compute, not between).
 * `submitAnswersAndStatusRefresh` = POST then /status refetch; toast on failure unless silent.
 * Concurrent calls: same version joins; different version keeps only the latest pending.
 */
export function useSubmitStrategyAnswersMutation(
  options?: UseSubmitStrategyAnswersMutationOptions
) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const onSubmittedAnswersVersionRef = useRef(options?.onSubmittedAnswersVersion)
  const submitQueueRef = useRef<AnswersSubmitQueue>(createAnswersSubmitQueue())
  useEffect(() => {
    onSubmittedAnswersVersionRef.current = options?.onSubmittedAnswersVersion
  }, [options?.onSubmittedAnswersVersion])

  const mutation = useMutation({
    mutationFn: submitStrategyQuestionnaireAnswers
  })
  const { mutateAsync } = mutation

  const submitAnswers = useCallback(
    async (vars: SubmitStrategyAnswersVars): Promise<void> => {
      await enqueueAnswersSubmit(submitQueueRef, vars.answersVersion, async () => {
        try {
          await mutateAsync(vars)
          // POST-only path — mark after POST succeeds (status comes later after compute)
          onSubmittedAnswersVersionRef.current?.(vars.answersVersion)
        } catch (error) {
          showToast({ message: EXPERT_REVIEW_SAVE_ERROR_TOAST, duration: 2500 })
          throw error
        }
      })
    },
    [mutateAsync, showToast]
  )

  const submitAnswersAndStatusRefresh = useCallback(
    async (
      vars: SubmitStrategyAnswersVars,
      { silent = false }: { silent?: boolean } = {}
    ): Promise<void> => {
      await enqueueAnswersSubmit(submitQueueRef, vars.answersVersion, async () => {
        await runExpertReviewSubmit(() => mutateAsync(vars), {
          queryClient,
          showToast,
          silent
        })
        // Only after POST + status — avoid MarkSubmitted when status refresh fails
        onSubmittedAnswersVersionRef.current?.(vars.answersVersion)
      })
    },
    [mutateAsync, queryClient, showToast]
  )

  return {
    ...mutation,
    submitAnswers,
    submitAnswersAndStatusRefresh
  }
}
